// Accessibility utilities and hooks
import { useState, useEffect } from 'react';

/**
 * Custom hook for managing accessibility features
 * @returns {Object} Accessibility state and utilities
 */
export const useAccessibility = () => {
  const [highContrast, setHighContrast] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);
  const [fontSize, setFontSize] = useState('normal');

  useEffect(() => {
    // Check for user's system preferences
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const prefersHighContrast = window.matchMedia('(prefers-contrast: high)');

    setReducedMotion(prefersReducedMotion.matches);
    setHighContrast(prefersHighContrast.matches);

    // Load saved preferences from localStorage
    const savedFontSize = localStorage.getItem('accessibility-font-size');
    if (savedFontSize) {
      setFontSize(savedFontSize);
    }

    const savedHighContrast = localStorage.getItem('accessibility-high-contrast');
    if (savedHighContrast) {
      setHighContrast(savedHighContrast === 'true');
    }

  }, [highContrast]);

  const toggleHighContrast = () => {
    const newValue = !highContrast;
    setHighContrast(newValue);
    localStorage.setItem('accessibility-high-contrast', newValue.toString());
  };

  const changeFontSize = (size) => {
    setFontSize(size);
    localStorage.setItem('accessibility-font-size', size);
  };

  return {
    highContrast,
    reducedMotion,
    fontSize,
    toggleHighContrast,
    changeFontSize
  };
};

/**
 * Utility function to announce content to screen readers
 * @param {string} message - Message to announce
 * @param {string} priority - Priority level ('polite' or 'assertive')
 */
export const announceToScreenReader = (message, priority = 'polite') => {
  const announcement = document.createElement('div');
  announcement.setAttribute('aria-live', priority);
  announcement.setAttribute('aria-atomic', 'true');
  announcement.style.position = 'absolute';
  announcement.style.left = '-10000px';
  announcement.style.width = '1px';
  announcement.style.height = '1px';
  announcement.style.overflow = 'hidden';

  document.body.appendChild(announcement);
  announcement.textContent = message;

  // Remove the element after announcement
  setTimeout(() => {
    document.body.removeChild(announcement);
  }, 1000);
};

/**
 * Focus management utility
 * @param {string} elementId - ID of element to focus
 */
export const focusElement = (elementId) => {
  const element = document.getElementById(elementId);
  if (element) {
    element.focus();
  } else {
    console.warn('Element not found for focus:', elementId);
  }
};

const AccessibilityUtils = {
  useAccessibility,
  announceToScreenReader,
  focusElement
};

export default AccessibilityUtils;
